import { ref, watch } from 'vue';
import { getWhatsNewData as fetchData } from '@/infrastructure/api-functions';
import type { IWhatsNewResponse } from '@/types';
import { useRoute } from 'vue-router';
import { useGlobalState } from './use-global-store';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useI18n } from 'vue-i18n';
import { hookErrorCode } from '@eureka/services/src/custom-error-handler';
import { LIMITED_PERMISSION_CODE } from '@/constant';

export function useWhatsNew() {
    const { t } = useI18n();
    const { checkedChild, companyId, updateLimitInfo } = useGlobalState();

    const route = useRoute();
    const query = route.query.q;

    const queryData = ref<IWhatsNewResponse[]>([]);
    const last90DayData = ref<IWhatsNewResponse[]>([]);
    const isQueryLoading = ref<boolean>(false);
    const isLastDayLoading = ref<boolean>(false);
    const queryRequestId = ref(0)
    const lastDayRequestId = ref(0)

    function getWhatsNewData(query = '') {
        const getWhatsNewData = fetchData({
            child: checkedChild.value,
            company_id: companyId.value,
            query,
        })
        return hookErrorCode(getWhatsNewData, LIMITED_PERMISSION_CODE, () => {
            updateLimitInfo(true)
        });
    }

    async function getQueryData() {
        isQueryLoading.value = true;
        queryRequestId.value++;
        const currentRequestId = queryRequestId.value;
        const data = await getWhatsNewData(query as string)
        if (currentRequestId === queryRequestId.value && data) {
            queryData.value = data
            isQueryLoading.value = false;
        }
    }

    async function getLast90DayData() {
        isLastDayLoading.value = true;
        lastDayRequestId.value++;
        const currentRequestId = lastDayRequestId.value;
        const data = await getWhatsNewData()
        if (currentRequestId === lastDayRequestId.value && data) {
            last90DayData.value = data
            isLastDayLoading.value = false;
        }
    }

    async function handleViewMore(data: {
        solution_ids: string[] | undefined, footerField?: string
    }) {
        const { solution_ids, footerField } = data;
        setTimeout(() => {
            openRightAside({
                title: t('patent'),
                data: {
                    company_id: companyId.value,
                    solution_ids,
                },
                footerField,
            })
        }, 0);
    }

    watch(() => checkedChild.value, () => {
        if (query) {
            getQueryData()
        }
        getLast90DayData();
    }, { immediate: true });

    return {
        getWhatsNewData,
        queryData,
        last90DayData,
        isQueryLoading,
        isLastDayLoading,
        handleViewMore,
    };
}
